import React from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { useCriancas } from '../hooks/useCriancas';
import { formatarDataBR } from '../common/data';
import PdiSection from './PdiSection';
import TerapiasSection from './TerapiasSection';

/**
 * Os seis valores são os da coluna `tipo` (ENUM) em `atendimentos_especiais`.
 * Enquanto este campo era um input livre ("Ex: Fonoaudiologia"), nenhum texto
 * digitado pelo gestor cabia no ENUM — e a tela nunca chegou a descobrir isso,
 * porque parava antes, no 403 do aluno.
 */
const TIPOS = [
  { valor: 'necessidade_especial',        label: 'Necessidade especial' },
  { valor: 'acompanhamento_pedagogico',   label: 'Acompanhamento pedagógico' },
  { valor: 'acompanhamento_psicologico',  label: 'Acompanhamento psicológico' },
  { valor: 'acompanhamento_medico',       label: 'Acompanhamento médico' },
  { valor: 'alergia',                     label: 'Alergia' },
  { valor: 'restricao_alimentar',         label: 'Restrição alimentar' },
] as const;

function rotuloTipo(valor?: string) {
  return TIPOS.find((t) => t.valor === valor)?.label || valor || '—';
}

interface AtendimentoEspecial {
  id: number;
  id_aluno: number;
  tipo: string;
  descricao: string;
  orientacoes?: string | null;
  plano_individualizado?: string | null;
  criado_em?: string;
  // Vêm montados pelo backend (leftJoinAndSelect), não são enviados no POST.
  aluno?: { id: number; nome: string } | null;
  registrado_por?: { id: number; nome?: string } | null;
}

export default function AtendimentosEspeciaisPage() {
  const { criancas } = useCriancas();

  return (
    <>
    <TabelaCrud<AtendimentoEspecial>
      titulo="Inclusão / AEE"
      endpoint="/atendimentos-especiais"
      colunas={[
        { chave: 'id', label: 'ID' },
        {
          chave: 'aluno',
          label: 'Criança',
          // O backend devolve o aluno montado; o id cru não diz nada ao gestor.
          render: (item) => item.aluno?.nome || `ID ${item.id_aluno}`,
        },
        { chave: 'tipo', label: 'Tipo', render: (item) => rotuloTipo(item.tipo) },
        {
          chave: 'plano_individualizado',
          label: 'Anotação de PDI',
          // Este é o campo de texto ANTIGO, mantido para não perder o que já foi
          // escrito. O PDI de verdade — metas, prazos e revisões — vive na seção
          // abaixo, e o texto daqui foi migrado para lá.
          render: (item) => (item.plano_individualizado?.trim() ? 'há texto' : '—'),
        },
        { chave: 'criado_em', label: 'Registrado em', render: (item) => formatarDataBR(item.criado_em) },
      ]}
      itemVazio={{ id_aluno: 0, tipo: '', descricao: '', orientacoes: '', plano_individualizado: '' }}
      validateItem={(item) => {
        if (!item.id_aluno || Number(item.id_aluno) <= 0) return 'Selecione a criança do atendimento.';
        if (!item.tipo) return 'Selecione o tipo de atendimento.';
        if (!item.descricao?.trim()) return 'Descreva o atendimento.';
        return null;
      }}
      modalLargo
      renderFormulario={(item, onChange) => (
        <>
          <label>Criança
            <select value={item.id_aluno ?? 0} onChange={(e) => onChange('id_aluno', Number(e.target.value))}>
              <option value={0}>Selecione...</option>
              {criancas.map((crianca) => (
                <option key={crianca.id} value={crianca.id}>{crianca.nome}</option>
              ))}
            </select>
          </label>
          <label>Tipo
            <select value={item.tipo ?? ''} onChange={(e) => onChange('tipo', e.target.value)}>
              <option value="">Selecione...</option>
              {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </label>
          <label>Descrição
            <textarea
              value={item.descricao ?? ''}
              rows={3}
              placeholder="O que a criança apresenta e desde quando."
              onChange={(e) => onChange('descricao', e.target.value)}
            />
          </label>
          <label>Orientações à equipe
            <textarea
              value={item.orientacoes ?? ''}
              rows={3}
              placeholder="O que a equipe deve fazer no dia a dia (e o que não deve)."
              onChange={(e) => onChange('orientacoes', e.target.value)}
            />
          </label>
          <label>Anotação de PDI (texto livre)
            <textarea
              value={item.plano_individualizado ?? ''}
              rows={4}
              placeholder="Anotação rápida. O PDI com metas, prazos e revisões fica na seção abaixo."
              onChange={(e) => onChange('plano_individualizado', e.target.value)}
            />
          </label>
        </>
      )}
    />

    <PdiSection />
    <TerapiasSection />
    </>
  );
}
